<script setup lang="ts">
import { computed, ref } from 'vue'
import { ArrowUp, ArrowDown, ArrowLeft } from '@element-plus/icons-vue'
import { useI18n } from '#imports'
import ChipSelectGroup from '@/components/SearchResult/ChipSelectGroup.vue'
import FilterGroup from '@/components/MenuNav/FilterGroup.vue'
import { useSearchQueryStore } from '@/stores/searchQuery.js'
import { useFilterStore } from '@/stores/filter.js'
import { useCityStore } from '@/stores/city.js'
import { useCategoryStore } from '@/stores/category.js'
import { useSearchWithStore } from '@/composables/useSearchWithStore'
import { peopleList, peopleCapRange, priceList, priceRange } from '@/config/common.js'

const emit = defineEmits<{
  collapse: []
}>()

const { t } = useI18n()
const searchQueryStore = useSearchQueryStore()
const filterStore = useFilterStore()
const cityStore = useCityStore()
const categoryStore = useCategoryStore()
const { handleSearchWithStore } = useSearchWithStore()

const usageCategoryId = import.meta.env.VITE_APP_BASE_USAGE_CATEGORY_ID

interface RangeValue {
  min?: number
  max?: number
  index?: string
}

const cityList = computed(() => cityStore.getCityList)
const usageList = computed(() => categoryStore.getUsageCategories)
const equipmentList = computed(() => filterStore.getEquipmentList)
const serviceList = computed(() => filterStore.getServiceList)

const showMore = ref(false)

function reSearch(): void {
  searchQueryStore.initGeoSearch()
  searchQueryStore.setPagination({ page: 1 })
  handleSearchWithStore()
}

function findRangeId(range: RangeValue, config: Record<number, RangeValue>): number {
  if (!range || Object.keys(range).length === 0) {
    return 0
  }
  const matched = Object.keys(config).find((key) => {
    const cfg = config[Number(key)]
    return cfg?.min === range?.min && cfg?.max === range?.max
  })
  return matched === undefined ? 0 : Number(matched)
}

// 縣市
const selectedCity = computed<number | ''>(() => searchQueryStore.getSearchCityId?.[0] ?? '')
function onCityChange(value: number | ''): void {
  if (value === '' || value === null || value === undefined) {
    searchQueryStore.clearCitySelected()
  } else {
    searchQueryStore.setSelectedCityID([value])
  }
  reSearch()
}

// 用途
const selectedUsage = computed<number | ''>(() => searchQueryStore.getSearchCategoryIds?.[usageCategoryId]?.[0] ?? '')
function onUsageChange(value: number | ''): void {
  if (value === '' || value === null || value === undefined) {
    searchQueryStore.clearCategoryObjectByKey(usageCategoryId)
  } else {
    searchQueryStore.setSelectedCategoryObject({ [usageCategoryId]: [value] })
  }
  reSearch()
}

// 人數
const selectedPeopleId = computed(() => findRangeId(searchQueryStore.getPeopleCapRange, peopleCapRange))
function onPeopleChange(id: number): void {
  searchQueryStore.setSelectedPeopleCapRange(peopleCapRange[id] ?? {})
  reSearch()
}
const peopleHelper = computed(() => {
  const min = peopleCapRange[selectedPeopleId.value]?.min
  return min ? t('filter.people_helper', { t: min }) : ''
})

// 價格
const selectedPriceId = computed(() => findRangeId(searchQueryStore.getPriceRange, priceRange))
function onPriceChange(id: number): void {
  searchQueryStore.setSelectedPriceRange(priceRange[id] ?? {})
  reSearch()
}
const priceHelper = computed(() => {
  const max = priceRange[selectedPriceId.value]?.max
  return max && max < 999999 ? t('filter.price_helper', { t: max }) : ''
})

// 更多篩選:設備/服務(切換後立即套用)
function toggleEquipment(item: { selected?: boolean }): void {
  item.selected = !item.selected
  searchQueryStore.setEquipmentIds(filterStore.getSelectedEquipment)
  reSearch()
}
function toggleService(item: { selected?: boolean }): void {
  item.selected = !item.selected
  searchQueryStore.setServiceIds(filterStore.getSelectedService)
  reSearch()
}

const selectedCount = computed(() => filterStore.getFilterSelectedTotal)
</script>

<template>
  <aside class="search-sidebar">
    <div class="sidebar-header"
         @click="emit('collapse')">
      <el-icon :size="18"><ArrowLeft /></el-icon>
      <span class="ml-[6px]">{{ $t('filter.hide_filter') }}</span>
      <span v-if="selectedCount"
            class="sidebar-count">{{ selectedCount }}</span>
    </div>

    <!-- 地區 -->
    <div class="sidebar-section">
      <div class="sidebar-label">
        <svg-icon name="city" size="16" class="mr-[5px]" />{{ $t('menu.location') }}
      </div>
      <el-select :model-value="selectedCity"
                 class="sidebar-select"
                 clearable
                 :placeholder="$t('menu.search_city')"
                 @change="onCityChange">
        <el-option v-for="item in cityList"
                   :key="item.city_id"
                   :label="item.city_name"
                   :value="item.city_id" />
      </el-select>
    </div>

    <!-- 用途 -->
    <div class="sidebar-section">
      <div class="sidebar-label">
        <svg-icon name="category" size="16" class="mr-[5px]" />{{ $t('menu.usage') }}
      </div>
      <el-select :model-value="selectedUsage"
                 class="sidebar-select"
                 clearable
                 :placeholder="$t('menu.usage')"
                 @change="onUsageChange">
        <el-option v-for="item in usageList"
                   :key="item.id"
                   :label="item.name"
                   :value="item.id" />
      </el-select>
    </div>

    <!-- 人數 -->
    <div class="sidebar-section">
      <ChipSelectGroup :title="`${$t('filter.people_title')}（${$t('filter.people_sub')}）`"
                       :options="peopleList"
                       :model-value="selectedPeopleId"
                       :helper-text="peopleHelper"
                       @update:model-value="onPeopleChange">
        <template #icon>
          <svg-icon name="people" size="16" />
        </template>
      </ChipSelectGroup>
    </div>

    <!-- 價格 -->
    <div class="sidebar-section">
      <ChipSelectGroup :title="`${$t('filter.price_title')}（${$t('filter.price_hour')}）`"
                       :options="priceList"
                       :model-value="selectedPriceId"
                       :helper-text="priceHelper"
                       @update:model-value="onPriceChange">
        <template #icon>
          <span class="sidebar-dollar">$</span>
        </template>
      </ChipSelectGroup>
    </div>

    <!-- 更多篩選條件 -->
    <div class="sidebar-section">
      <div class="sidebar-more-toggle"
           @click="showMore = !showMore">
        <span>{{ $t('filter.more_filter') }}</span>
        <el-icon :size="18">
          <ArrowDown v-if="!showMore" />
          <ArrowUp v-else />
        </el-icon>
      </div>
      <div v-show="showMore"
           class="mt-[16px]">
        <FilterGroup v-if="equipmentList?.length"
                     :title="$t('filter.equipment_title')"
                     :list="equipmentList"
                     :handle-click="toggleEquipment" />
        <FilterGroup v-if="serviceList?.length"
                     class="mt-[20px]"
                     :title="$t('filter.service_title')"
                     :list="serviceList"
                     :handle-click="toggleService" />
      </div>
    </div>
  </aside>
</template>

<style scoped lang="scss">
.search-sidebar {
  @apply w-[300px] flex-shrink-0 h-full overflow-y-auto border-r border-osn-grey-03 px-[20px] py-[16px];

  .sidebar-header {
    @apply flex items-center text-[14px] font-semibold text-osn-dark-grey-00 cursor-pointer;

    .sidebar-count {
      @apply ml-[8px] min-w-[22px] h-[22px] leading-[22px] px-[6px] text-center text-[12px]
      text-osn-dark-grey-00 bg-osn-yellow-00 rounded-full;
    }
  }

  .sidebar-section {
    @apply mt-[20px];

    .sidebar-label {
      @apply mb-[8px] flex items-center text-[14px] font-semibold text-osn-dark-grey-00;
    }

    .sidebar-select {
      @apply w-full;
    }

    .sidebar-dollar {
      @apply inline-flex items-center justify-center w-[16px] h-[16px] text-[13px] font-bold text-osn-dark-grey-00;
    }

    .sidebar-more-toggle {
      @apply flex items-center justify-between text-[14px] font-semibold text-osn-dark-grey-00 cursor-pointer;
    }
  }
}
</style>
